import type { ActivityEvent, Execution, Message, ReviewDecision, WorkItem, WorkItemDetail } from '@deskworks/shared-types';
import { ACTIVE_EXECUTION_STATUSES, TERMINAL_WORK_ITEM_STATUSES } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Bot, CircleCheck, CircleX, ClipboardList, Crown, GitBranch, Info, MessageCircleQuestion, MessageSquare, Moon, Send, ShieldCheck, User, Zap } from 'lucide-react';
import { Fragment, useState, type ReactNode } from 'react';
import { activityIcon } from '@/components/activity-feed';
import { Markdown } from '@/components/markdown';
import { Section } from '@/components/section';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { formatAbsolute, formatDay, formatTime } from '@/lib/format';
import { REVIEW_DECISION } from '@/lib/labels';
import { inSentence, t } from '@/lib/i18n';
import { useApiMutation, useIsPhone } from '@/lib/hooks';
import { useChatInput } from '@/lib/use-chat-input';
import { ChatScreen } from '@/components/chat/chat-screen';
import { qk } from '@/lib/query-keys';
import { useAutoScroll } from '@/lib/use-auto-scroll';
import { cn } from '@/lib/utils';
import { SessionLink } from '@/components/session-link';
import { AgentSteps, currentTurn } from '@/components/agent-steps';
import { readMarks, ReadMark, type Mark } from '@/components/chat/read-mark';
import { AgentAvatar } from '@/components/agent-avatar';
import { VickiActionCard } from '@/components/front-desk/vicki-action-card';
import { WorkItemStatusBadge } from '@/components/status-badge';
import { Course, wokenReasons } from './course';

export function isAgentRunning(item: WorkItemDetail): boolean {
  return !!item.currentExecution && ACTIVE_EXECUTION_STATUSES.includes(item.currentExecution.status);
}

/**
 * A person can talk to the desk's Lead beside the work (docs/features/work-item-chat.md#talking-to-the-lead) — not
 * where the thread is a chat with the Lead anyway: a finished item's follow-up, an open question.
 */
function canTalkToLead(item: WorkItemDetail): boolean {
  if (!item.lead || item.kind === 'review' || TERMINAL_WORK_ITEM_STATUSES.includes(item.status)) return false;
  return !(item.questionOpen && item.status === 'waiting' && !item.paused);
}

export function Chat({
  item,
  showSteps = true,
  messages,
  activity,
  isPending,
  error,
  onRetry,
}: {
  item: WorkItemDetail;
  /** The steps of a follow-up's current turn; off where another card on the page already shows them. */
  showSteps?: boolean;
  messages: Message[] | undefined;
  /** What happened besides the messages — woven into the thread in the order it happened. */
  activity?: ActivityEvent[];
  isPending: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [leadChosen, setLeadChosen] = useState(false);
  // The Lead's own runs: what it decided after each result (docs/features/work-item-chat.md#lead-and-agents).
  const runs = useQuery({ queryKey: qk.workItemExecutions(item.id), queryFn: () => api.workItems.executions(item.id) });
  // By run, newest first (the default); everything in the order it happened; or only the Lead and its agents.
  const [view, setView] = useState<'course' | 'all' | 'team'>('course');
  const all = timeline(item, messages ?? [], activity ?? [], runs.data ?? []);
  const entries = view === 'team' ? all.filter(betweenLeadAndAgents) : all;
  const course = view === 'course';
  // The course starts with the newest at the top; the other views keep to the bottom, where the newest is.
  const ref = useAutoScroll<HTMLDivElement>(entries.length, !course);
  const running = isAgentRunning(item);
  const terminal = TERMINAL_WORK_ITEM_STATUSES.includes(item.status);
  const leadPossible = canTalkToLead(item);
  const toLead = leadPossible && leadChosen;
  const lead = item.lead?.name ?? t('the Lead');
  const sideChat = !!item.sideChat && ACTIVE_EXECUTION_STATUSES.includes(item.sideChat.status);

  const send = useApiMutation((content: string) => api.workItems.sendMessage(item.id, toLead ? { content, toLead } : { content }), {
    errorTitle: t('Couldn’t send message'),
    onSuccess: (msg) => {
      setDraft('');
      queryClient.setQueryData<Message[]>(qk.workItemMessages(item.id), (old) =>
        old && !old.some((m) => m.id === msg.id) ? [...old, msg] : old,
      );
    },
    invalidate: [qk.workItem(item.id)],
  });

  const submit = () => {
    const content = draft.trim();
    if (content && !send.isPending) send.mutate(content);
  };
  const input = useChatInput(draft, submit);
  const phone = useIsPhone();
  // On a phone the thread opens as a chat of its own (docs/features/mobile.md#chats); the page shows its last messages.
  const [open, setOpen] = useState(false);

  const followUp = running && item.currentExecution?.kind === 'follow_up';
  // The run stays a while for more questions; it is only working while the person spoke last.
  const turn = followUp && showSteps && item.currentExecution ? currentTurn(messages ?? [], item.currentExecution.createdAt) : null;
  // A run on the work (not a follow-up) shows what it is doing at the end of the thread.
  const working = running && !followUp ? item.currentExecution : null;
  // What the agent at work read of what the person wrote to it (a run: since it started; a follow-up: since its reply).
  const marks = working || turn?.open ? readMarks(messages ?? [], item.currentExecution, working ? 'run' : 'reply') : null;
  const agentName = item.currentExecution?.agentName ?? item.assignedAgentName ?? t('The agent');
  let hint: { icon: ReactNode; text: string; tone: string };
  if (toLead && sideChat) {
    hint = {
      icon: <Zap className="size-3.5" />,
      text: t('{lead} is here and answering beside the work — the run in progress goes on.', { lead }),
      tone: 'text-indigo-700 dark:text-indigo-300',
    };
  } else if (toLead) {
    hint = {
      icon: <Crown className="size-3.5" />,
      text: item.sideChatRequested
        ? t('{lead} is being woken up to answer you …', { lead })
        : t('{lead} answers you here beside the work — the run in progress goes on. What you decide, {lead} passes on to the work.', { lead }),
      tone: 'text-indigo-700 dark:text-indigo-300',
    };
  } else if (followUp) {
    hint = {
      icon: <Zap className="size-3.5" />,
      text: t('The agent is here and answering — it stays a few minutes for follow-up questions.'),
      tone: 'text-indigo-700 dark:text-indigo-300',
    };
  } else if ((terminal || item.questionOpen) && item.followUpRequested) {
    hint = {
      icon: <Zap className="size-3.5" />,
      text: t('The agent is being woken up to answer you …'),
      tone: 'text-indigo-700 dark:text-indigo-300',
    };
  } else if (running) {
    hint = {
      icon: <Zap className="size-3.5" />,
      text: t('The agent is working right now and will see your message immediately.'),
      tone: 'text-indigo-700 dark:text-indigo-300',
    };
  } else if (item.questionOpen && !item.paused) {
    hint = {
      icon: <MessageCircleQuestion className="size-3.5" />,
      text: t('A question is open — your message is answered here, and the work goes on once it is answered.'),
      tone: 'text-amber-700 dark:text-amber-300',
    };
  } else if (terminal) {
    hint = {
      icon: <Info className="size-3.5" />,
      text: t('Finished — ask the agent about the result, or ask for changes and it reopens the Work Item.'),
      tone: 'text-muted-foreground',
    };
  } else if (item.paused) {
    hint = {
      icon: <Moon className="size-3.5" />,
      text: t('This Work Item is paused. The agent will read your message once you resume it.'),
      tone: 'text-muted-foreground',
    };
  } else {
    hint = {
      icon: <Moon className="size-3.5" />,
      text: t('The agent is asleep — your message will wake it up.'),
      tone: 'text-muted-foreground',
    };
  }

  // By run, all of it, or only what the Lead and its agents said to each other.
  const viewSwitch = (
    <div className="inline-flex rounded-md border p-0.5" role="group" aria-label={t('Show')}>
      <Button
        size="xs"
        variant={course ? 'secondary' : 'ghost'}
        onClick={() => setView('course')}
        title={t('Newest first: each run with what it was asked to do, what it found and what followed.')}
      >
        {t('By run')}
      </Button>
      <Button size="xs" variant={view === 'all' ? 'secondary' : 'ghost'} onClick={() => setView('all')}>
        {t('Everything')}
      </Button>
      <Button size="xs" variant={view === 'team' ? 'secondary' : 'ghost'} onClick={() => setView('team')} title={t('Only what the Lead and its agents said to each other: briefs, results and the Lead’s decisions.')}>
        <Crown /> {t('Lead ↔ agents')}
      </Button>
    </div>
  );
  const steps = (
    <>
      {working && <AgentSteps executionId={working.id} working agentName={item.assignedAgentName ?? t('The agent')} />}
      {turn && (
        <AgentSteps executionId={item.currentExecution?.id} turn={turn} working={turn.open} agentName={item.assignedAgentName ?? t('The agent')} />
      )}
    </>
  );
  const message = (m: Message) => <ChatMessage message={m} workspaceId={item.workspaceId} mark={marks?.get(m.id)} agentName={agentName} />;
  const thread = isPending ? (
    <LoadingRows rows={2} />
  ) : error ? (
    <ErrorState error={error} onRetry={onRetry} />
  ) : course ? (
    <Course
      item={item}
      messages={(messages ?? []).filter((m) => !childrenDone(m))}
      activity={activity ?? []}
      runs={runs.data ?? []}
      task={<OpeningBrief item={item} />}
      live={turn && <AgentSteps executionId={item.currentExecution?.id} turn={turn} working={turn.open} agentName={item.assignedAgentName ?? t('The agent')} />}
      renderMessage={message}
      renderChild={(child, back) => <SubItemEntry child={child} back={back} parent={item} />}
    />
  ) : entries.length === 0 && !working ? (
    <EmptyState compact title={t('No messages yet')} description={t('Write to the agent whenever you want to tell it something.')} />
  ) : (
    <div className="flex flex-col gap-3">
      {entries.map((e, i) => (
        <Fragment key={e.key}>
          {(i === 0 || e.day !== entries[i - 1].day) && <DayDivider at={e.at} />}
          {e.kind === 'message' ? (
            message(e.message)
          ) : e.kind === 'brief' ? (
            <OpeningBrief item={item} />
          ) : e.kind === 'child' ? (
            <SubItemEntry child={e.child} back={e.back} parent={item} />
          ) : e.kind === 'lead' ? (
            <LeadDecision run={e.run} workspaceId={item.workspaceId} />
          ) : (
            <ActivityGroup events={e.events} workspaceId={item.workspaceId} />
          )}
        </Fragment>
      ))}
      {steps}
    </div>
  );

  const composer = (
    <div className="border-t bg-muted/30 px-3 pt-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] sm:px-5 sm:py-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className={cn('flex min-w-0 items-center gap-1.5 text-xs', hint.tone)}>
          <span className="shrink-0">{hint.icon}</span>
          <span className="min-w-0">{hint.text}</span>
        </div>
        {leadPossible && (
          <label
            className={cn(
              'inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 text-xs',
              toLead ? 'border-indigo-300 bg-indigo-50 text-indigo-800 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200' : 'text-muted-foreground',
            )}
            title={t('Ask the Lead about the work, or change its course, without interrupting the agent at work.')}
          >
            <Crown className="size-3.5" />
            {t('Talk to {lead} (Lead)', { lead })}
            <Switch checked={toLead} onCheckedChange={setLeadChosen} className="ml-0.5" />
          </label>
        )}
      </div>
      <div className="flex items-end gap-2">
        <Textarea
          ref={input.ref}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={input.onKeyDown}
          placeholder={input.placeholder(
            toLead
              ? t('Message {lead} … (Enter to send, Shift+Enter for a new line)', { lead })
              : t('Message the agent … (Enter to send, Shift+Enter for a new line)'),
          )}
          rows={1}
          enterKeyHint={input.touch ? 'enter' : 'send'}
          autoFocus={phone && open}
          className="max-h-40 min-h-11 resize-none bg-card py-2.5 sm:max-h-48"
          aria-label={t('Message')}
        />
        <Button onClick={submit} loading={send.isPending} disabled={!draft.trim()} aria-label={t('Send message')} className="h-11">
          {!send.isPending && <Send />}
          <span className="hidden sm:inline">{t('Send')}</span>
        </Button>
      </div>
    </div>
  );

  if (phone) {
    const recent = entries.filter((e) => e.kind === 'message').slice(-2);
    return (
      <Section icon={<MessageSquare />} title={t('Thread')} contentClassName="px-0 pb-0" actions={viewSwitch}>
        <div className="flex flex-col gap-3 border-t px-3 py-3">
          {recent.map((e) => e.kind === 'message' && <Fragment key={e.key}>{message(e.message)}</Fragment>)}
          <Button onClick={() => setOpen(true)} className="w-full" size="lg">
            <MessageSquare /> {t('Open the thread')}
            {(messages?.length ?? 0) > 0 && <span className="text-primary-foreground/70 tabular-nums">{messages!.length}</span>}
          </Button>
        </div>
        {!open && (
          // In thumb reach wherever the page is scrolled to.
          <Button
            onClick={() => setOpen(true)}
            size="icon"
            aria-label={t('Open the thread')}
            className="fixed right-4 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-30 size-14 rounded-full shadow-lg [&_svg]:size-6"
          >
            <MessageSquare />
          </Button>
        )}
        {open && (
          <ChatScreen title={item.title} subtitle={agentName} onBack={() => setOpen(false)}>
            <div key={view} ref={ref} className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
              {thread}
            </div>
            {composer}
          </ChatScreen>
        )}
      </Section>
    );
  }

  return (
    <Section
      icon={<MessageSquare />}
      title={t('Thread')}
      description={
        course
          ? t('Newest first: each run with what it was asked to do, what it found and what followed; the task at the end. Write here to guide the work.')
          : t('How the work goes, in the order it happened: the task, what the Lead decided and handed to whom, what each agent did and reported, yours. Write here to guide the work.')
      }
      contentClassName="px-0 pb-0"
      actions={viewSwitch}
    >
      <div key={view} ref={ref} className="max-h-[min(78vh,900px)] min-h-48 overflow-y-auto overscroll-contain border-t px-3 py-4 sm:px-5">
        {thread}
      </div>
      {composer}
    </Section>
  );
}

type Entry =
  | { kind: 'brief'; key: string; at: string; day: string }
  | { kind: 'message'; key: string; at: string; day: string; message: Message }
  | { kind: 'activity'; key: string; at: string; day: string; events: ActivityEvent[] }
  | { kind: 'lead'; key: string; at: string; day: string; run: Execution }
  /** A sub-item handed out (back: false) and, once it ended, what came back (back: true). */
  | { kind: 'child'; key: string; at: string; day: string; child: WorkItem; back: boolean };

/** Steps the thread says better elsewhere: a worker starting or resuming, a run's start (its brief is a message). */
const QUIET_EVENTS = new Set(['agent.lifecycle', 'run.started']);

/** Deskworks' summary when the last sub-item ended; the sub-items stand in the thread themselves. */
function childrenDone(m: Message): boolean {
  return (
    m.senderType === 'system' &&
    (m.metadata?.event === 'children_done' || m.metadata?.event === 'child_done' || m.content.startsWith('All child Work Items are done'))
  );
}

/**
 * The task, then messages, activity, the Lead's decisions and the sub-items it handed out in the order they happened;
 * the steps between two messages (on one day) form one group. A Lead run shows where it ended, with what it concluded.
 */
function timeline(item: WorkItemDetail, messages: Message[], events: ActivityEvent[], runs: Execution[]): Entry[] {
  const decisions = runs.filter((r) => r.kind === 'lead' && r.result?.trim() && r.finishedAt);
  // Reviews tell in the thread themselves (review_requested / review_decision).
  const children = item.children.filter((c) => c.kind !== 'review');
  type X = { at: string; message?: Message; event?: ActivityEvent; run?: Execution; child?: WorkItem; back?: boolean };
  const all: X[] = [
    ...messages.filter((m) => !childrenDone(m)).map((m) => ({ at: m.createdAt, message: m })),
    ...events.filter((e) => !QUIET_EVENTS.has(e.type)).map((e) => ({ at: e.createdAt, event: e })),
    ...decisions.map((r) => ({ at: r.finishedAt!, run: r })),
    ...children.map((c) => ({ at: c.createdAt, child: c, back: false })),
    ...children.filter((c) => TERMINAL_WORK_ITEM_STATUSES.includes(c.status)).map((c) => ({ at: c.completedAt ?? c.updatedAt, child: c, back: true })),
  ];
  all.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime() || (a.event?.seq ?? 0) - (b.event?.seq ?? 0));
  const out: Entry[] = [{ kind: 'brief', key: 'brief', at: item.createdAt, day: new Date(item.createdAt).toDateString() }];
  for (const x of all) {
    const day = new Date(x.at).toDateString();
    const last = out.at(-1);
    if (x.message) out.push({ kind: 'message', key: x.message.id, at: x.at, day, message: x.message });
    else if (x.run) out.push({ kind: 'lead', key: x.run.id, at: x.at, day, run: x.run });
    else if (x.child) out.push({ kind: 'child', key: `${x.child.id}:${x.back ? 'back' : 'out'}`, at: x.at, day, child: x.child, back: !!x.back });
    else if (last?.kind === 'activity' && last.day === day) last.events.push(x.event!);
    else out.push({ kind: 'activity', key: x.event!.id, at: x.at, day, events: [x.event!] });
  }
  return out;
}

/** What the Lead and its agents said to each other: briefs, results, the Lead's decisions and notes, requests it passed on. */
const TEAM_EVENTS = new Set(['run_started', 'run_finished', 'lead_note', 'chat_request', 'side_chat_request', 'review_decision', 'talk_question']);
function betweenLeadAndAgents(e: Entry) {
  if (e.kind === 'lead' || e.kind === 'child' || e.kind === 'brief') return true;
  if (e.kind === 'activity') return false;
  if (e.message.metadata?.event === 'talk_answer') return true;
  return e.message.senderType === 'agent' && (TEAM_EVENTS.has(String(e.message.metadata?.event ?? '')) || !e.message.metadata?.sideChat);
}

/** One run of the Lead on the item: why it was woken and what it concluded, with its session. */
function LeadDecision({ run, workspaceId }: { run: Execution; workspaceId: string }) {
  const [open, setOpen] = useState(false);
  const reasons = wokenReasons(run.brief);
  const long = (run.result ?? '').length > 420;
  return (
    <div className="flex gap-2.5">
      <AgentAvatar seed={run.agentId} size={28} lead className="mt-0.5 max-sm:hidden" />
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <span className="font-medium text-foreground/80">{run.agentName ?? t('The Lead')}</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-1.5 py-px text-amber-800 dark:text-amber-300">
            <Crown className="size-3" /> {reasons ? t('decided after {reason}', { reason: reasons }) : t('decided')}
          </span>
          <time dateTime={run.finishedAt ?? undefined} title={run.finishedAt ? formatAbsolute(run.finishedAt, true) : undefined}>
            {run.finishedAt ? formatTime(run.finishedAt) : ''}
          </time>
          <SessionLink executionId={run.id} workspaceId={workspaceId} />
        </div>
        <div className={cn('rounded-2xl rounded-tl-sm border border-dashed bg-muted/40 px-3.5 py-2 text-sm', long && !open && 'max-h-36 overflow-hidden')}>
          <Markdown text={run.result ?? ''} />
        </div>
        {long && (
          <button type="button" onClick={() => setOpen((o) => !o)} className="mt-0.5 cursor-pointer text-xs text-muted-foreground hover:text-foreground hover:underline">
            {open ? t('Show less') : t('Show all')}
          </button>
        )}
      </div>
    </div>
  );
}

function DayDivider({ at }: { at: string }) {
  return (
    <div className="flex items-center gap-3 py-1 text-xs text-muted-foreground">
      <span aria-hidden className="h-px flex-1 bg-border" />
      {formatDay(at)}
      <span aria-hidden className="h-px flex-1 bg-border" />
    </div>
  );
}

/** Steps in a row, as quiet lines between the messages; a long run of them shows its last few until opened. */
function ActivityGroup({ events, workspaceId }: { events: ActivityEvent[]; workspaceId: string }) {
  const [open, setOpen] = useState(false);
  const hidden = open || events.length <= 4 ? 0 : events.length - 3;
  return (
    <ol className="flex flex-col gap-1.5">
      {hidden > 0 && (
        <li className="pl-9.5">
          <button type="button" onClick={() => setOpen(true)} className="text-xs text-muted-foreground hover:text-foreground hover:underline">
            {t('Show {count} earlier steps', { count: hidden })}
          </button>
        </li>
      )}
      {events.slice(hidden).map((e) => (
        <li key={e.id} className="flex items-start gap-2.5 text-xs text-muted-foreground">
          <span className="flex h-5 w-7 shrink-0 items-center justify-center">
            <span className="flex size-5 items-center justify-center rounded-full border bg-card [&_svg]:size-3">{activityIcon(e.type)}</span>
          </span>
          <div className="min-w-0 flex-1 pt-0.5">
            <span className="break-words whitespace-pre-wrap text-foreground/80">{e.message || e.type}</span>
            <span className="ml-2 inline-flex flex-wrap items-center gap-x-1.5">
              <time dateTime={e.createdAt} title={`${formatAbsolute(e.createdAt, true)} · ${e.type}`} className="tabular-nums">
                {formatTime(e.createdAt)}
              </time>
              {e.agentName && <span>· {e.agentName}</span>}
              {e.executionId && <SessionLink executionId={e.executionId} workspaceId={workspaceId} />}
            </span>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function ChatMessage({
  message: m,
  workspaceId,
  mark,
  agentName,
}: {
  message: Message;
  workspaceId: string;
  /** A person's message the agent at work has not answered yet: whether it read it. */
  mark?: Mark;
  agentName?: string;
}) {
  // A card of VICKI's in one of her tasks (docs/features/front-desk.md#tasks).
  if (m.senderType === 'system' && m.metadata?.event === 'vicki_action_requested' && typeof m.metadata.vickiActionId === 'string') {
    return <VickiActionCard workItemId={m.workItemId} actionId={m.metadata.vickiActionId} />;
  }
  if (m.senderType === 'system' && m.metadata?.event === 'talk_answer') return <TalkAnswer message={m} workspaceId={workspaceId} />;
  if (m.senderType === 'system' && m.metadata?.event !== 'review_decision') {
    // Handed over to another desk from the training: the note links to the Work Item there.
    const handoff = m.metadata?.handoff as { workItemId?: string; deskName?: string } | undefined;
    return (
      <div className="flex justify-center">
        <div className="max-w-[90%] rounded-lg bg-muted px-3 py-1 text-xs break-words text-muted-foreground" title={formatAbsolute(m.createdAt, true)}>
          <Markdown text={m.content} className="gap-1 text-xs" />
          {handoff?.workItemId && (
            <Link
              to="/w/$workspaceId/work-items/$workItemId"
              params={{ workspaceId, workItemId: handoff.workItemId }}
              className="mt-0.5 inline-block font-medium underline underline-offset-2 hover:text-foreground"
            >
              {handoff.deskName ? t('Open it in {desk}', { desk: handoff.deskName }) : t('Open the Work Item')}
            </Link>
          )}
        </div>
      </div>
    );
  }
  if (m.metadata?.event === 'side_chat_request') return <PassedOn message={m} workspaceId={workspaceId} />;
  const mine = m.senderType === 'user';
  const brief = m.metadata?.event === 'run_started';
  return (
    <div className={cn('flex gap-2.5', mine && 'flex-row-reverse')}>
      {!mine && m.senderId ? (
        <AgentAvatar seed={m.senderId} size={28} className="mt-0.5 max-sm:hidden" />
      ) : (
        <div
          className={cn(
            'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full max-sm:hidden',
            mine ? 'bg-primary text-primary-foreground' : 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-300',
          )}
        >
          {mine ? <User className="size-3.5" /> : <Bot className="size-3.5" />}
        </div>
      )}
      {/* On a phone a message takes the width: the avatar moves into the name line, yours stay on the right. */}
      <div className={cn('flex max-w-[80%] min-w-0 flex-col', mine ? 'items-end max-sm:max-w-[88%]' : 'max-sm:max-w-full')}>
        <div className="mb-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {!mine && <Bot className="size-3.5 text-indigo-600 sm:hidden dark:text-indigo-300" />}
          <span className="font-medium text-foreground/80">{m.senderName || (mine ? t('You') : t('Agent'))}</span>
          <ReviewTag message={m} workspaceId={workspaceId} />
          {brief && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 px-1.5 py-px font-medium text-indigo-700 dark:text-indigo-300">
              <ArrowRight className="size-3" /> {typeof m.metadata?.agentName === 'string' ? t('task for {agent}', { agent: m.metadata.agentName }) : t('started a run')}
            </span>
          )}
          {m.metadata?.event === 'program_report' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/10 px-1.5 py-px font-medium text-sky-700 dark:text-sky-300">
              <Info className="size-3" /> {t('report')}
            </span>
          )}
          {m.metadata?.event === 'talk_question' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/10 px-1.5 py-px font-medium text-sky-700 dark:text-sky-300">
              <MessageCircleQuestion className="size-3" /> {t('asks {name}', { name: String((m.metadata.to as { name?: string } | undefined)?.name ?? t('an agent')) })}
            </span>
          )}
          {m.metadata?.toLead === true && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 px-1.5 py-px text-indigo-700 dark:text-indigo-300">
              <Crown className="size-3" /> {t('to the Lead')}
            </span>
          )}
          {m.metadata?.sideChat === true && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 px-1.5 py-px text-indigo-700 dark:text-indigo-300">
              <Crown className="size-3" /> {t('beside the work')}
            </span>
          )}
          {m.metadata?.event === 'lead_note' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 px-1.5 py-px text-indigo-700 dark:text-indigo-300">
              <Zap className="size-3" /> {typeof m.metadata?.agentName === 'string' ? t('to {agent}', { agent: m.metadata.agentName }) : t('to the agent at work')}
            </span>
          )}
          {m.metadata?.event === 'side_chat_request' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 px-1.5 py-px text-indigo-700 dark:text-indigo-300">
              <Zap className="size-3" /> {t('for the next run')}
            </span>
          )}
          {m.metadata?.question === true && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-1.5 py-px text-amber-700 dark:text-amber-300">
              <MessageCircleQuestion className="size-3" /> {m.metadata?.forLead === true ? t('question for the Lead') : t('question')}
            </span>
          )}
          {m.metadata?.event === 'run_finished' && (
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-1.5 py-px font-medium',
                m.metadata?.outcome === 'failed' ? 'bg-red-500/10 text-red-700 dark:text-red-300' : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
              )}
            >
              <CircleCheck className="size-3" /> {m.metadata?.outcome === 'failed' ? t('run failed') : t('result for the Lead')}
            </span>
          )}
          <time dateTime={m.createdAt} title={formatAbsolute(m.createdAt, true)}>
            {formatTime(m.createdAt)}
          </time>
          {!mine && <SessionLink executionId={m.executionId} workspaceId={workspaceId} />}
        </div>
        <div
          className={cn(
            'rounded-2xl px-3.5 py-2 text-sm break-words',
            mine ? 'rounded-tr-sm bg-primary whitespace-pre-wrap text-primary-foreground' : 'rounded-tl-sm border bg-card',
            brief && 'border-indigo-200 bg-indigo-50/50 dark:border-indigo-900 dark:bg-indigo-950/30',
            m.metadata?.event === 'run_finished' && m.metadata?.outcome !== 'failed' && 'border-emerald-200 dark:border-emerald-900',
            m.metadata?.event === 'review_decision' && reviewTone(m.metadata.decision),
          )}
        >
          {mine ? m.content : (
            <Expandable long={m.content.length > 1200}>
              <Markdown text={m.content} />
            </Expandable>
          )}
        </div>
        {mark && <ReadMark mark={mark} agentName={agentName ?? t('The agent')} />}
      </div>
    </div>
  );
}

/** Agents reviewing each other: "asked X for a review" / the reviewer's decision, linked to the review sub-item. */
function ReviewTag({ message: m, workspaceId }: { message: Message; workspaceId: string }) {
  const event = m.metadata?.event;
  const reviewId = typeof m.metadata?.reviewWorkItemId === 'string' ? m.metadata.reviewWorkItemId : null;
  if ((event !== 'review_requested' && event !== 'review_decision') || !reviewId) return null;
  const decision = m.metadata?.decision as ReviewDecision | null | undefined;
  const reviewer = m.metadata?.reviewerName == null ? null : String(m.metadata.reviewerName);
  const round = Number(m.metadata?.round) > 1 ? String(m.metadata?.round) : null;
  const label =
    event === 'review_requested'
      ? reviewer
        ? round
          ? t('asked {reviewer} for a review (round {round})', { reviewer, round })
          : t('asked {reviewer} for a review', { reviewer })
        : round
          ? t('asked an agent for a review (round {round})', { round })
          : t('asked an agent for a review')
      : decision
        ? // German keeps the decision's capitals (nouns).
          t('review: {decision}', { decision: inSentence(REVIEW_DECISION[decision]) })
        : t('review: no decision');
  return (
    <Link
      to="/w/$workspaceId/work-items/$workItemId"
      params={{ workspaceId, workItemId: reviewId }}
      className="inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-px hover:text-primary"
      title={t('Open the review')}
    >
      <ShieldCheck className="size-3" /> {label}
    </Link>
  );
}

function reviewTone(decision: unknown): string {
  if (decision === 'approved') return 'border-emerald-300 dark:border-emerald-800';
  if (decision === 'changes_requested') return 'border-amber-300 dark:border-amber-800';
  return '';
}

/** A long text shows its beginning until opened. */
function Expandable({ long, children }: { long: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  if (!long) return <>{children}</>;
  return (
    <>
      <div className={cn(!open && 'relative max-h-64 overflow-hidden')}>
        {children}
        {!open && <div aria-hidden className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-card to-transparent" />}
      </div>
      <button type="button" onClick={() => setOpen((o) => !o)} className="mt-1 cursor-pointer text-xs font-medium text-primary hover:underline">
        {open ? t('Show less') : t('Show all')}
      </button>
    </>
  );
}

/** What the thread is about: the task as it was given, at its start. */
function OpeningBrief({ item }: { item: WorkItemDetail }) {
  const description = item.description?.trim();
  const goal = item.goal?.trim();
  const from = item.createdByType === 'user' ? t('from a person') : item.createdByType === 'agent' ? t('from an agent') : t('from a trigger');
  return (
    <div className="rounded-xl border bg-muted/30 px-4 py-3">
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        <ClipboardList className="size-3.5" />
        <span className="font-medium text-foreground/80">{t('The task')}</span>
        <span>{from}</span>
        <time dateTime={item.createdAt} title={formatAbsolute(item.createdAt, true)}>
          {formatTime(item.createdAt)}
        </time>
      </div>
      {description ? (
        <Expandable long={description.length > 700}>
          <div className="text-sm">
            <Markdown text={description} />
          </div>
        </Expandable>
      ) : (
        <p className="text-sm">{item.title}</p>
      )}
      {goal && (
        <p className="mt-2 border-t pt-2 text-[13px]">
          <span className="font-medium">{t('Done when:')}</span> {goal}
        </p>
      )}
    </div>
  );
}

/** The Lead took something a person said beside the work on for its next run: one quiet line, the text when opened. */
function PassedOn({ message: m, workspaceId }: { message: Message; workspaceId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-start gap-2.5 text-xs text-muted-foreground">
      <span className="flex h-5 w-7 shrink-0 items-center justify-center">
        <Crown className="size-3.5 text-amber-600" />
      </span>
      <div className="min-w-0 flex-1 pt-0.5">
        <button type="button" onClick={() => setOpen((o) => !o)} className="cursor-pointer text-left hover:text-foreground">
          {t('{lead} took it on for the next run', { lead: m.senderName ?? t('The Lead') })}
          <span className="ml-1.5 underline-offset-2 hover:underline">{open ? t('Hide') : t('Show')}</span>
        </button>
        <span className="ml-2 inline-flex items-center gap-1.5">
          <time dateTime={m.createdAt} title={formatAbsolute(m.createdAt, true)} className="tabular-nums">
            {formatTime(m.createdAt)}
          </time>
          <SessionLink executionId={m.executionId} workspaceId={workspaceId} />
        </span>
        {open && (
          <div className="mt-1.5 rounded-lg border bg-card px-3 py-2 text-sm text-foreground">
            <Markdown text={m.content} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A sub-item in its parent's thread (an assignment, a hand-off to another desk): when the Lead handed it out, and once it
 * ended, what came back. Its own thread opens in place.
 */
function SubItemEntry({ child, back, parent }: { child: WorkItem; back: boolean; parent: WorkItemDetail }) {
  const [open, setOpen] = useState(false);
  const otherDesk = child.deskId !== parent.deskId;
  const who = otherDesk ? (child.deskName ?? t('another desk')) : (child.assignedAgentName ?? t('the Lead'));
  const link = (
    <Link
      to="/w/$workspaceId/work-items/$workItemId"
      params={{ workspaceId: child.workspaceId, workItemId: child.id }}
      className="font-medium text-foreground hover:text-primary hover:underline"
    >
      {child.title}
    </Link>
  );
  if (back) {
    const failed = child.status !== 'complete';
    return (
      <div className="flex gap-2.5">
        <span
          className={cn(
            'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full max-sm:hidden',
            failed ? 'bg-red-500/15 text-red-600' : 'bg-emerald-500/15 text-emerald-600',
          )}
        >
          {failed ? <CircleX className="size-4" /> : <CircleCheck className="size-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <span className="font-medium text-foreground/80">{who}</span>
            <span>{failed ? t('could not finish') : t('finished')}</span>
            {link}
            {child.completedAt && (
              <time dateTime={child.completedAt} title={formatAbsolute(child.completedAt, true)}>
                {formatTime(child.completedAt)}
              </time>
            )}
          </div>
          <div className={cn('rounded-2xl rounded-tl-sm border bg-card px-3.5 py-2 text-sm', failed ? 'border-red-200 dark:border-red-900' : 'border-emerald-200 dark:border-emerald-900')}>
            {child.result?.trim() ? (
              <Expandable long={child.result.length > 900}>
                <Markdown text={child.result} />
              </Expandable>
            ) : (
              <p className="text-muted-foreground">{failed ? t('It ended without a result.') : t('No result was saved.')}</p>
            )}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-dashed px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
        <GitBranch className="size-4 shrink-0 text-indigo-500" />
        <span className="text-muted-foreground">{otherDesk ? t('Handed to {desk}:', { desk: who }) : t('Assignment for {agent}:', { agent: who })}</span>
        {link}
        <WorkItemStatusBadge status={child.status} paused={child.paused} />
        <time className="text-xs text-muted-foreground" dateTime={child.createdAt} title={formatAbsolute(child.createdAt, true)}>
          {formatTime(child.createdAt)}
        </time>
        <button type="button" onClick={() => setOpen((o) => !o)} className="ml-auto cursor-pointer text-xs text-muted-foreground hover:text-foreground hover:underline">
          {open ? t('Hide its thread') : t('Show its thread')}
        </button>
      </div>
      {open && <ChildThread child={child} />}
    </div>
  );
}

function ChildThread({ child }: { child: WorkItem }) {
  const messages = useQuery({ queryKey: qk.workItemMessages(child.id), queryFn: () => api.workItems.messages(child.id) });
  const shown = (messages.data ?? []).filter((m) => !childrenDone(m));
  return (
    <div className="mt-2 flex max-h-96 flex-col gap-3 overflow-y-auto rounded-lg bg-muted/30 p-3">
      {messages.isPending ? (
        <LoadingRows rows={2} />
      ) : messages.isError ? (
        <ErrorState error={messages.error} onRetry={() => void messages.refetch()} />
      ) : shown.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('No messages yet')}</p>
      ) : (
        shown.map((m) => <ChatMessage key={m.id} message={m} workspaceId={child.workspaceId} />)
      )}
    </div>
  );
}

/**
 * An answer to what the Lead asked from this Work Item (docs/features/desk-talk.md): one of its own agents, or another
 * desk, answering in its own words; the whole exchange stays in the chat it links to.
 */
function TalkAnswer({ message: m, workspaceId }: { message: Message; workspaceId: string }) {
  const meta = m.metadata as { agentId?: string; agentName?: string; deskName?: string; answer?: string; failed?: boolean; talkId?: string; team?: boolean };
  const name = meta.agentName ?? meta.deskName ?? t('Agent');
  const text = typeof meta.answer === 'string' && meta.answer.trim() ? meta.answer : m.content;
  return (
    <div className="flex gap-2.5">
      {meta.agentId ? (
        <AgentAvatar seed={meta.agentId} size={28} className="mt-0.5 max-sm:hidden" />
      ) : (
        <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-sky-500/15 text-sky-600 max-sm:hidden dark:text-sky-300">
          <Bot className="size-3.5" />
        </div>
      )}
      <div className="flex max-w-[80%] min-w-0 flex-col max-sm:max-w-full">
        <div className="mb-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <span className="font-medium text-foreground/80">{name}</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/10 px-1.5 py-px font-medium text-sky-700 dark:text-sky-300">
            <MessageSquare className="size-3" /> {meta.failed ? t('no answer') : t('answers the Lead')}
          </span>
          <time dateTime={m.createdAt} title={formatAbsolute(m.createdAt, true)}>
            {formatTime(m.createdAt)}
          </time>
          {meta.talkId && (
            <Link to="/w/$workspaceId/conversations/$conversationId" params={{ workspaceId, conversationId: meta.talkId }} className="hover:text-foreground hover:underline">
              {meta.team ? t('Team chat') : t('Chat')}
            </Link>
          )}
        </div>
        <div className={cn('rounded-2xl rounded-tl-sm border bg-card px-3.5 py-2 text-sm break-words', meta.failed ? 'border-amber-200 dark:border-amber-900' : 'border-sky-200 dark:border-sky-900')}>
          <Expandable long={text.length > 1200}>
            <Markdown text={text} />
          </Expandable>
        </div>
      </div>
    </div>
  );
}
